import { Link } from "components/ui/link";
import { format } from "date-fns";
import { ChevronRight, Sparkles } from "lucide-react";
import React from "react";
import { graphql, useFragment } from "react-relay";
import { useNavigate } from "react-router";
import { JobStatusTag } from "src/components/JobStatusTag";
import { ScopeGate } from "src/components/ScopeGate";
import { Button } from "src/components/ui/button";
import { PageHeader } from "src/components/ui/PageHeader";

import { formatCronExpression } from "../utils";
import { DigestJobInfo_job$key } from "./__generated__/DigestJobInfo_job.graphql";

const digestJobInfoFragment = graphql`
  fragment DigestJobInfo_job on GraphQLJob {
    id
    name
    status
    schedule
    nextRunAt
    team {
      id
      name
    }
    latestPromptFeedback {
      id
      suggestions {
        name
        isStale
      }
    }
  }
`;

interface DigestJobInfoProps {
  job: DigestJobInfo_job$key;
  isRunningDigest: boolean;
  onRunNow: () => void;
  onEdit: () => void;
  onStatusChanged: () => void;
  canModify: boolean;
}

const DigestJobInfo: React.FC<DigestJobInfoProps> = React.memo(function DigestJobInfo({
  job: jobData,
  isRunningDigest,
  onRunNow,
  onEdit,
  onStatusChanged,
  canModify
}) {
  const job = useFragment(digestJobInfoFragment, jobData);
  const navigate = useNavigate();

  const applicableSuggestionCount = (job.latestPromptFeedback?.suggestions ?? []).filter((s) => !s.isStale).length;
  const hasSuggestions = applicableSuggestionCount > 0;

  const handleOpenPromptStudio = () => {
    const url = hasSuggestions
      ? `/studio?digestJobId=${job.id}&suggestion=true`
      : `/studio?digestJobId=${job.id}`;
    navigate(url);
  };

  const formatSchedule = (schedule: any) => {
    if (!schedule || typeof schedule !== "object") {
      return "No schedule";
    }

    const cronExpression = schedule.cron_expression || schedule.cron;
    if (!cronExpression) {
      return "No schedule";
    }

    return formatCronExpression(cronExpression) || cronExpression;
  };

  return (
    <PageHeader
      title={job.name}
      breadcrumb={
        <>
          <Link
            to="/digests"
            className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
          >
            Digest Agents
          </Link>
          <ChevronRight className="mx-2 w-3 h-3" />
          <span className="text-muted-foreground">{job.name || "Digest"}</span>
        </>
      }
      badges={
        job.status && (
          <JobStatusTag
            jobId={job.id}
            status={job.status}
            onStatusChanged={onStatusChanged}
            readOnly={!canModify}
          />
        )
      }
      actions={
        <>
          <ScopeGate scope="write:prompt_studio">
            <Button
              variant={hasSuggestions ? "default" : "outline"}
              size="sm"
              onClick={handleOpenPromptStudio}
            >
              {hasSuggestions && <Sparkles className="h-3 w-3 mr-1" />}
              {hasSuggestions
                ? `Review ${applicableSuggestionCount} Suggestion${applicableSuggestionCount !== 1 ? "s" : ""}`
                : "Open Prompt Studio"}
            </Button>
          </ScopeGate>
          <ScopeGate scope="read:digests">
            <Button
              variant="outline"
              size="sm"
              onClick={onRunNow}
              disabled={isRunningDigest}
            >
              {isRunningDigest ? "Running..." : "Run"}
            </Button>
          </ScopeGate>
          <ScopeGate scope="write:digests">
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
            >
              Edit
            </Button>
          </ScopeGate>
        </>
      }
      details={
        <>
          <div>
            <span className="text-muted-foreground">Team:</span> {job.team?.name || "No team"}
          </div>
          <div>
            <span className="text-muted-foreground">Schedule:</span> {formatSchedule(job.schedule)}
          </div>
          <div>
            <span className="text-muted-foreground">Next Run:</span>{" "}
            {job.status !== "ACTIVE" ? (
              "(Paused)"
            ) : job.nextRunAt ? (
              (() => {
                const nextRunDate = new Date(job.nextRunAt);
                const utcTime = format(new Date(nextRunDate.getTime() + nextRunDate.getTimezoneOffset() * 60000), "MMM d, HH:mm");
                const localTime = format(nextRunDate, "MMM d, HH:mm");
                return `${utcTime} UTC (${localTime} Local)`;
              })()
            ) : (
              "Not scheduled"
            )}
          </div>
        </>
      }
    />
  );
});

export default DigestJobInfo;